/**
 * @fileoverview ClubOS Settings page — club profile, users, notifications, integrations, security
 */

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";

// ClubOS components
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

import { settingsService } from "api";
import FootballLoader from "components/FootballLoader";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

const TABS = ["Club Profile", "Users & Roles", "Notifications", "Integrations", "Security", "Super Admin"];

const SITE_SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: "dashboard", description: "Main dashboard with KPIs, season form, alerts and analytics widgets" },
  { id: "finance", label: "Finance Module", icon: "account_balance", description: "Contracts, transfers, budget, revenue, matchday revenue" },
  { id: "squad", label: "Squad Management", icon: "groups", description: "Squad, player profiles, registrations" },
  { id: "coaching", label: "Coaching", icon: "sports_soccer", description: "Tactics, training, match logs" },
  { id: "scouting", label: "Scouting", icon: "radar", description: "Pipeline, scout reports, player comparison" },
  { id: "academy", label: "Youth Academy", icon: "school", description: "Players, batches, coaches, attendance, fees, injuries" },
];

const USERS = [
  { id: 1, name: "Thomas Hargreaves", role: "Head of Football Operations", email: "t.hargreaves@fcmetro.com", status: "Active" },
  { id: 2, name: "Claire Svensson",   role: "Chief Scout",                 email: "c.svensson@fcmetro.com",   status: "Active" },
  { id: 3, name: "Marcus Reid",       role: "Finance Director",            email: "m.reid@fcmetro.com",        status: "Active" },
  { id: 4, name: "Priya Nair",        role: "Analyst",                     email: "p.nair@fcmetro.com",        status: "Active" },
  { id: 5, name: "Jamie Oduya",       role: "Technical Director",          email: "j.oduya@fcmetro.com",       status: "Pending" },
];

const ROLE_COLOR = {
  "Head of Football Operations": "dark",
  "Chief Scout": "info",
  "Finance Director": "success",
  "Analyst": "secondary",
  "Technical Director": "warning",
};

const INTEGRATIONS = [
  { name: "Wyscout",    category: "Scouting",  status: "Connected",    icon: "visibility",         color: "#1a73e8" },
  { name: "Hudl",       category: "Video",     status: "Connected",    icon: "videocam",            color: "#f97316" },
  { name: "StatsBomb",  category: "Analytics", status: "Connected",    icon: "analytics",           color: "#7c3aed" },
  { name: "Xero",       category: "Finance",   status: "Not Connected",icon: "account_balance",     color: "#13b5ea" },
  { name: "DocuSign",   category: "Contracts", status: "Not Connected",icon: "draw",                color: "#FFB900" },
  { name: "InStat",     category: "Scouting",  status: "Not Connected",icon: "bar_chart",           color: "#e53935" },
];

const NOTIFICATIONS = [
  { id: 1, label: "Contract expiry alerts (90 days)",     enabled: true },
  { id: 2, label: "FSR compliance warnings",              enabled: true },
  { id: 3, label: "Transfer deadline reminders",          enabled: true },
  { id: 4, label: "New scout reports submitted",          enabled: true },
  { id: 5, label: "Player injury updates",                enabled: false },
  { id: 6, label: "Match result notifications",           enabled: true },
  { id: 7, label: "Registration deadline alerts",         enabled: false },
  { id: 8, label: "Weekly financial summary email",       enabled: true },
];

function Settings() {
  const [activeTab, setActiveTab] = useState(0);
  const [notifications, setNotifications] = useState(NOTIFICATIONS);
  const [clubName, setClubName] = useState("FC Metropolitan");
  const [league, setLeague] = useState("Premier Division");
  const [country, setCountry] = useState("England");
  const [founded, setFounded] = useState("1893");
  const [stadium, setStadium] = useState("Metropolitan Arena");
  const [capacity, setCapacity] = useState("42,500");
  const [enabledSections, setEnabledSections] = useState({
    dashboard: true, finance: true, squad: true, coaching: true, scouting: true, academy: true,
  });
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesSaving, setModulesSaving]   = useState(false);
  const [modulesMsg, setModulesMsg]         = useState(null); // { type, text }

  // Load module config from DB on mount
  useEffect(() => {
    setModulesLoading(true);
    settingsService.getModules()
      .then((data) => {
        if (Array.isArray(data)) {
          const map = {};
          data.forEach((m) => { map[m.moduleId] = m.isEnabled; });
          setEnabledSections((prev) => {
            const merged = { ...prev, ...map };
            localStorage.setItem("clubos_enabled_sections", JSON.stringify(merged));
            return merged;
          });
        }
      })
      .catch(() => {/* API unavailable — use defaults */})
      .finally(() => setModulesLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleNotification = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, enabled: !n.enabled } : n))
    );
  };

  const toggleSection = (sectionId) => {
    setEnabledSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
    setModulesMsg(null);
  };

  const saveModules = async () => {
    setModulesSaving(true);
    setModulesMsg(null);
    try {
      const modules = Object.entries(enabledSections).map(([moduleId, isEnabled]) => ({ moduleId, isEnabled }));
      await settingsService.saveModules(modules);
      localStorage.setItem("clubos_enabled_sections", JSON.stringify(enabledSections));
      setModulesMsg({ type: "success", text: "Module settings saved. Reload the page to update the navigation menu." });
    } catch {
      setModulesMsg({ type: "error", text: "Failed to save. Please try again." });
    } finally {
      setModulesSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Settings
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            System configuration · FC Metropolitan
          </SoftTypography>
        </SoftBox>

        <Card>
          <SoftBox sx={{ borderBottom: "1px solid #f0f0f0" }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ px: 3, pt: 1 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {TABS.map((t) => (
                <Tab key={t} label={t} sx={{ textTransform: "none" }} />
              ))}
            </Tabs>
          </SoftBox>

          <SoftBox p={3}>
            {/* ── Club Profile ── */}
            {activeTab === 0 && (
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="medium" mb={3}>
                  Club Profile
                </SoftTypography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Club Name" value={clubName} onChange={(e) => setClubName(e.target.value)} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="League / Division" value={league} onChange={(e) => setLeague(e.target.value)} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Country" value={country} onChange={(e) => setCountry(e.target.value)} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Year Founded" value={founded} onChange={(e) => setFounded(e.target.value)} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Stadium" value={stadium} onChange={(e) => setStadium(e.target.value)} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Capacity" value={capacity} onChange={(e) => setCapacity(e.target.value)} size="small" />
                  </Grid>
                  <Grid item xs={12}>
                    <SoftButton variant="gradient" color="info" size="small">
                      Save Club Profile
                    </SoftButton>
                  </Grid>
                </Grid>
              </SoftBox>
            )}

            {/* ── Users & Roles ── */}
            {activeTab === 1 && (
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <SoftTypography variant="h6" fontWeight="medium">
                    Users &amp; Roles
                  </SoftTypography>
                  <SoftButton variant="gradient" color="info" size="small">
                    Invite User
                  </SoftButton>
                </SoftBox>
                {USERS.map((user) => (
                  <SoftBox
                    key={user.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    p={2}
                    mb={1}
                    borderRadius="lg"
                    sx={{ border: "1px solid #e9ecef", "&:hover": { backgroundColor: "#f8f9fa" } }}
                  >
                    <SoftBox display="flex" alignItems="center" gap={2}>
                      <SoftBox
                        width={40}
                        height={40}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ backgroundColor: "#e3f2fd" }}
                      >
                        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#1565c0", fontSize: "0.75rem" }}>
                          {user.name.split(" ").map((n) => n[0]).join("").substring(0, 2)}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox>
                        <SoftTypography variant="button" fontWeight="medium" display="block">
                          {user.name}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text">
                          {user.email}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox display="flex" alignItems="center" gap={1.5}>
                      <SoftBadge badgeContent={user.role} color={ROLE_COLOR[user.role] || "secondary"} size="xs" container />
                      <SoftBadge badgeContent={user.status} color={user.status === "Active" ? "success" : "warning"} size="xs" container />
                      <SoftButton variant="outlined" color="secondary" size="small">
                        Edit
                      </SoftButton>
                    </SoftBox>
                  </SoftBox>
                ))}
              </SoftBox>
            )}

            {/* ── Notifications ── */}
            {activeTab === 2 && (
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="medium" mb={3}>
                  Notification Preferences
                </SoftTypography>
                {notifications.map((n) => (
                  <SoftBox
                    key={n.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    py={1.5}
                    sx={{ borderBottom: "1px solid #f5f5f5" }}
                  >
                    <SoftTypography variant="button" fontWeight={n.enabled ? "medium" : "regular"} color={n.enabled ? "dark" : "text"}>
                      {n.label}
                    </SoftTypography>
                    <SoftBox
                      onClick={() => toggleNotification(n.id)}
                      sx={{
                        width: 44,
                        height: 24,
                        borderRadius: 12,
                        backgroundColor: n.enabled ? "#17c1e8" : "#e9ecef",
                        cursor: "pointer",
                        position: "relative",
                        transition: "background-color 0.2s",
                        flexShrink: 0,
                      }}
                    >
                      <SoftBox
                        sx={{
                          position: "absolute",
                          top: 2,
                          left: n.enabled ? 22 : 2,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: "white",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                          transition: "left 0.2s",
                        }}
                      />
                    </SoftBox>
                  </SoftBox>
                ))}
                <SoftBox mt={3}>
                  <SoftButton variant="gradient" color="info" size="small">
                    Save Preferences
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            )}

            {/* ── Integrations ── */}
            {activeTab === 3 && (
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="medium" mb={3}>
                  Third-Party Integrations
                </SoftTypography>
                <Grid container spacing={2}>
                  {INTEGRATIONS.map((integration) => {
                    const connected = integration.status === "Connected";
                    return (
                      <Grid key={integration.name} item xs={12} sm={6} md={4}>
                        <SoftBox
                          p={2.5}
                          borderRadius="lg"
                          sx={{
                            border: `1px solid ${connected ? "#a5d6a7" : "#e9ecef"}`,
                            backgroundColor: connected ? "#f1f8e9" : "white",
                          }}
                        >
                          <SoftBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
                            <SoftBox
                              width={40}
                              height={40}
                              borderRadius="md"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{ backgroundColor: integration.color + "18", border: `1px solid ${integration.color}33` }}
                            >
                              <Icon sx={{ color: integration.color, fontSize: "1.2rem" }}>
                                {integration.icon}
                              </Icon>
                            </SoftBox>
                            <SoftBox>
                              <SoftTypography variant="button" fontWeight="bold" display="block" lineHeight={1.2}>
                                {integration.name}
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text">
                                {integration.category}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                          <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                            <SoftBadge
                              badgeContent={integration.status}
                              color={connected ? "success" : "secondary"}
                              size="xs"
                              container
                            />
                            <SoftButton
                              variant={connected ? "outlined" : "gradient"}
                              color={connected ? "error" : "info"}
                              size="small"
                            >
                              {connected ? "Disconnect" : "Connect"}
                            </SoftButton>
                          </SoftBox>
                        </SoftBox>
                      </Grid>
                    );
                  })}
                </Grid>
              </SoftBox>
            )}

            {/* ── Security ── */}
            {activeTab === 4 && (
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="medium" mb={3}>
                  Security Settings
                </SoftTypography>
                <Grid container spacing={3} maxWidth={500}>
                  <Grid item xs={12}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                      CHANGE PASSWORD
                    </SoftTypography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Current Password" type="password" size="small" />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="New Password" type="password" size="small" />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Confirm New Password" type="password" size="small" />
                      </Grid>
                      <Grid item xs={12}>
                        <SoftButton variant="gradient" color="info" size="small">
                          Update Password
                        </SoftButton>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                      TWO-FACTOR AUTHENTICATION
                    </SoftTypography>
                    <SoftBox
                      p={2}
                      borderRadius="lg"
                      sx={{ border: "1px solid #e9ecef", backgroundColor: "#f8f9fa" }}
                    >
                      <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                        <SoftBox>
                          <SoftTypography variant="button" fontWeight="medium" display="block">
                            Authenticator App
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text">
                            Use an authenticator app for 2FA
                          </SoftTypography>
                        </SoftBox>
                        <SoftButton variant="gradient" color="success" size="small">
                          Enable 2FA
                        </SoftButton>
                      </SoftBox>
                    </SoftBox>
                  </Grid>

                  <Grid item xs={12}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                      ACTIVE SESSIONS
                    </SoftTypography>
                    {[
                      { device: "Chrome on Windows", location: "London, UK", current: true },
                      { device: "Safari on iPhone", location: "Manchester, UK", current: false },
                    ].map((session, i) => (
                      <SoftBox
                        key={i}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        p={1.5}
                        mb={1}
                        borderRadius="md"
                        sx={{ border: "1px solid #e9ecef" }}
                      >
                        <SoftBox>
                          <SoftTypography variant="button" fontWeight="medium" display="block">
                            {session.device}
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text">{session.location}</SoftTypography>
                        </SoftBox>
                        {session.current ? (
                          <SoftBadge badgeContent="Current Session" color="success" size="xs" container />
                        ) : (
                          <SoftButton variant="outlined" color="error" size="small">
                            Revoke
                          </SoftButton>
                        )}
                      </SoftBox>
                    ))}
                  </Grid>
                </Grid>
              </SoftBox>
            )}

            {/* ── Super Admin ── */}
            {activeTab === 5 && (
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="medium" mb={1}>
                  Module Access Control
                </SoftTypography>
                <SoftTypography variant="caption" color="text" display="block" mb={3}>
                  Enable or disable sections of the application. Changes are saved to the database and applied to all users.
                </SoftTypography>

                {modulesLoading ? (
                  <FootballLoader label="Loading module settings…" />
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {SITE_SECTIONS.map((section) => (
                        <Grid item xs={12} key={section.id}>
                          <SoftBox
                            display="flex" justifyContent="space-between" alignItems="center"
                            p={2} borderRadius="lg"
                            sx={{ border: "1px solid #e9ecef", backgroundColor: enabledSections[section.id] ? "rgba(76,175,80,0.05)" : "rgba(0,0,0,0.02)" }}
                          >
                            <SoftBox display="flex" alignItems="center" gap={2}>
                              <Icon sx={{ color: enabledSections[section.id] ? "success.main" : "text.disabled", fontSize: "1.5rem" }}>
                                {section.icon}
                              </Icon>
                              <SoftBox>
                                <SoftTypography variant="button" fontWeight="medium" display="block">
                                  {section.label}
                                </SoftTypography>
                                <SoftTypography variant="caption" color="text">
                                  {section.description}
                                </SoftTypography>
                              </SoftBox>
                            </SoftBox>
                            <Switch
                              checked={!!enabledSections[section.id]}
                              onChange={() => toggleSection(section.id)}
                              color="success"
                            />
                          </SoftBox>
                        </Grid>
                      ))}
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {modulesMsg && (
                      <Alert severity={modulesMsg.type} sx={{ mb: 2 }}>{modulesMsg.text}</Alert>
                    )}

                    <SoftBox display="flex" justifyContent="flex-end">
                      <SoftButton variant="gradient" color="info" onClick={saveModules} disabled={modulesSaving}>
                        {modulesSaving ? "Saving…" : "Save Module Settings"}
                      </SoftButton>
                    </SoftBox>
                  </>
                )}
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />
    </DashboardLayout>
  );
}

export default Settings;
